import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { IconButton } from "@mui/material";
import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import CustomTable from "../../../../../../app/CustomHeadlessTable/CustomTable";
import CustomInputComponent from "../../../../../../app/CustomHeadlessTable/components/CustomInputComponent";
import {
  addWaterUsage34_1cLCA,
  deleteWaterUsage34_1cLCA,
  getAllWaterUsage34_1cLCA,
  updateWaterUsage34_1cLCA,
} from "../../../../../../apis/Product/CustomManufacturingFlow34Api";
import { getProductWaterType34_1cDropdown } from "../../../../../../apis/Dropdown";
import { TableCompareTrigger } from "../../../components/lcaComparison";

// Impact categories configuration (16)
const IMPACT_CATEGORIES = [
  { name: "Climate change", unit: "kg CO₂-eq", key: "global_warming" },
  { name: "Ozone depletion", unit: "kg CFC-11-eq", key: "ozone_depletion" },
  { name: "Photochemical ozone formation", unit: "kg NMVOC-eq", key: "smog" },
  { name: "Acidification", unit: "mol H⁺-eq", key: "acidification" },
  {
    name: "Eutrophication, terrestrial",
    unit: "mol N-eq",
    key: "eutrophication_terrestrial",
  },
  {
    name: "Eutrophication, freshwater",
    unit: "kg P-eq",
    key: "eutrophication_freshwater",
  },
  {
    name: "Eutrophication, marine",
    unit: "kg N-eq",
    key: "eutrophication_marine",
  },
  {
    name: "Human toxicity, cancer effects",
    unit: "CTUh",
    key: "carcinogenics",
  },
  {
    name: "Human toxicity, non-cancer effects",
    unit: "CTUh",
    key: "non_carcinogenics",
  },
  {
    name: "Particulate matter",
    unit: "disease incidence",
    key: "respiratory_effects",
  },
  { name: "Ecotoxicity, freshwater", unit: "CTUe", key: "ecotoxicity" },
  {
    name: "Resource use, energy carriers",
    unit: "MJ",
    key: "fossil_fuel_depletion",
  },
  { name: "Land use", unit: "Pt", key: "land_use" },
  { name: "Water use", unit: "m³ world-eq deprived", key: "water_use" },
  {
    name: "Resource use, mineral and metals",
    unit: "kg Sb-eq",
    key: "resource_use_minerals_metals",
  },
  {
    name: "Ionising radiation, human health",
    unit: "kBq U235-eq",
    key: "ionising_radiation",
  },
];

const WaterUsage34_1cTable = ({ productId }) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [currentImpactCategoryIndex, setCurrentImpactCategoryIndex] =
    useState(0);
  const [waterTypeDropdown, setWaterTypeDropdown] = useState([]);

  // Fetch water type dropdown (Flow 34.1c)
  useEffect(() => {
    getProductWaterType34_1cDropdown({
      setDropdown: setWaterTypeDropdown,
    });
  }, []);

  const toggleColumnVisibility = useCallback((columnId) => {
    setHiddenColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId]
    );
  }, []);

  const fetchData = useCallback(() => {
    if (!productId) return;
    getAllWaterUsage34_1cLCA({
      productId,
      setObjects: setData,
    });
  }, [productId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleEdit = useCallback(
    (editedItem) => {
      const waterTypeCode =
        typeof editedItem.water_type === "string"
          ? editedItem.water_type
          : editedItem.water_type?.value || editedItem.water_type?.code || null;
      updateWaterUsage34_1cLCA({
        productId,
        rowId: editedItem.id,
        water_type: waterTypeCode,
        supplier: editedItem.supplier || null,
        qty: editedItem.qty || 0,
        objects: data,
        setObjects: setData,
        use_swal: true,
      });
    },
    [productId, data]
  );

  const handleAdd = useCallback(
    (newItem) => {
      const waterTypeCode =
        typeof newItem.water_type === "string"
          ? newItem.water_type
          : newItem.water_type?.value || newItem.water_type?.code || null;
      addWaterUsage34_1cLCA({
        productId,
        water_type: waterTypeCode,
        supplier: newItem.supplier || null,
        qty: newItem.qty || 0,
        callback: fetchData,
        use_swal: true,
      });
    },
    [productId, fetchData]
  );

  const handleDelete = useCallback(
    (itemToDelete) => {
      deleteWaterUsage34_1cLCA({
        productId,
        rowId: itemToDelete.id,
        callback: fetchData,
      });
    },
    [productId, fetchData]
  );

  const currentImpactCategory = IMPACT_CATEGORIES[currentImpactCategoryIndex];
  const impactCategoryAccessor = currentImpactCategory.key;

  const handlePreviousImpactCategory = useCallback(() => {
    setCurrentImpactCategoryIndex((prev) =>
      prev === 0 ? IMPACT_CATEGORIES.length - 1 : prev - 1
    );
  }, []);

  const handleNextImpactCategory = useCallback(() => {
    setCurrentImpactCategoryIndex((prev) =>
      prev === IMPACT_CATEGORIES.length - 1 ? 0 : prev + 1
    );
  }, []);

  const resolveWaterTypeUnit = useCallback(
    (rowData) => {
      const code =
        typeof rowData?.water_type === "string"
          ? rowData.water_type
          : rowData?.water_type?.value || rowData?.water_type?.code;
      if (!code || !waterTypeDropdown.length) return "m³";
      const w = waterTypeDropdown.find(
        (o) =>
          !o.isCategory &&
          (o.code === code || o.value === code || o.name === code)
      );
      return w?.unit || "m³";
    },
    [waterTypeDropdown]
  );

  const columns = useMemo(
    () => [
      {
        id: "water_type",
        accessor: "water_type",
        header: `${t("TYPE_OF_WATER") || "Type of water"} *`,
        type: "dropdown",
        isCategory: true,
        placeholder: t("SELECT_WATER_TYPE") || "Select water type...",
        width: "130px",
        unit: (rowId, rowData) => resolveWaterTypeUnit(rowData),
      },
      {
        id: "supplier",
        accessor: "supplier",
        header: t("WATER_SUPPLIER") || "Water Supplier",
        type: "text",
        placeholder: t("USER_TYPES") || "User types...",
        optional: true,
      },
      {
        id: "qty",
        accessor: "qty",
        header: `${t("QTY_FUNC_UNIT") || "Quantity per functional unit"} *`,
        type: "number",
        placeholder: t("USER_TYPES") || "User types...",
        unit: (rowId, rowData) => resolveWaterTypeUnit(rowData),
      },
      {
        id: currentImpactCategory.key,
        header: (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <IconButton
              size="small"
              onClick={handlePreviousImpactCategory}
              style={{ padding: "4px" }}
            >
              <KeyboardArrowLeft fontSize="small" />
            </IconButton>
            <span>
              {currentImpactCategory.name} [{currentImpactCategory.unit}]
            </span>
            <IconButton
              size="small"
              onClick={handleNextImpactCategory}
              style={{ padding: "4px" }}
            >
              <KeyboardArrowRight fontSize="small" />
            </IconButton>
            <TableCompareTrigger />
          </div>
        ),
        accessor: currentImpactCategory.key,
        type: "custom",
        optional: true, // read-only output
        canSort: false,
        cell: ({ row }) => {
          const currentCategory = IMPACT_CATEGORIES[currentImpactCategoryIndex];
          const impactValue =
            row.original[currentCategory.key] ||
            row.original[`impact_${currentCategory.key}`] ||
            row.original.impact_categories?.[currentCategory.key] ||
            0;
          return <CustomInputComponent value={impactValue} readOnly />;
        },
        subtotalUnit: currentImpactCategory.unit,
        subtotalFixed: true,
        width: "200px",
      },
    ],
    [
      t,
      currentImpactCategory,
      currentImpactCategoryIndex,
      handlePreviousImpactCategory,
      handleNextImpactCategory,
      resolveWaterTypeUnit,
    ]
  );

  const disableRules = useMemo(
    () => ({
      [impactCategoryAccessor]: () => true,
    }),
    [impactCategoryAccessor]
  );

  const subtotalColumns = useMemo(
    () => ({
      [impactCategoryAccessor]: {
        label: `Total ${currentImpactCategory.name}`,
      },
    }),
    [impactCategoryAccessor, currentImpactCategory.name]
  );

  const memoizedDropdownData = useMemo(
    () => ({
      water_type: waterTypeDropdown || [],
    }),
    [waterTypeDropdown]
  );

  return (
    <CustomTable
      columns={columns}
      data={data}
      setData={setData}
      onAdd={handleAdd}
      onEdit={handleEdit}
      onDelete={handleDelete}
      dropdownData={memoizedDropdownData}
      subtotalColumns={subtotalColumns}
      customInputs={useMemo(
        () => ({ [impactCategoryAccessor]: CustomInputComponent }),
        [impactCategoryAccessor]
      )}
      comparisonConfig={{ categories: IMPACT_CATEGORIES }}
      hiddenColumns={hiddenColumns}
      toggleColumnVisibility={toggleColumnVisibility}
      disableRules={disableRules}
    />
  );
};

export default WaterUsage34_1cTable;
